// ReportsPage.jsx — 第一百零二批（SB-106「页面呈现」）：项目汇报的**读面**。
//
// ── 它读的是什么 ────────────────────────────────────────────────────────────
//   GET /api/project-reports?projectGroupId          → 清单（无正文）
//   GET /api/project-reports/<key>?projectGroupId    → 单篇 + 正文 + 最近修订
// 汇报是库里的实体，不是 data_content 里的文档：不经发布管线、不受 view glob 授权，
// 可见性只有一条——调用者是不是这个组的成员。写入侧是别的项目（peer 工作空间的
// agent 凭 dck_ key 走 PUT），所以这个页面**只读**，一个写入控件都不该有：能写的
// 那条路在 CLI 上，且它的主体是由凭据推导的（via_agent），页面代写会毁掉这份归属。
//
// ── 组从哪来 ────────────────────────────────────────────────────────────────
// `useAuth().currentGroup`——不自己取 groups[0]。那是单组时代的化石，第一百零一批
// 刚把最后三处拆掉（SB-042 的 firstGroup 注释里点名的三页）。切组即换数据：两条
// effect 的依赖里都带着 currentGroup?.id。
//
// ── 正文用同一只渲染器 ──────────────────────────────────────────────────────
// <MarkdownBody> 就是工单/工作项正文那一只，而它内部又是发布管线的 md-render。
// 三个面同源的理由见 MarkdownBody 头注：另写一只渲染器意味着同一段 markdown 在
// 不同页面可以长得不一样，而那种差异没有任何判据看得住。
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
// agent 徽标（SB-091 §4③：via_agent 由凭据推导，页面显示它才让推导有落点）。
// 第一百零四批起是共享件：组间通信的消息流用同一枚，理由见 AgentChip.jsx 头注。
import AgentChip from '../components/AgentChip.jsx';
import HelpLink from '../components/HelpLink.jsx';
import MarkdownBody from '../components/MarkdownBody.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { filterByTag, normalizeReports, normalizeRevisions, pickReportKey, tagsOf } from './reports.mjs';

export default function ReportsPage() {
  const t = useT();
  const { lang } = useLang();
  const { currentGroup } = useAuth();
  // 攥在手里的只是"用户点过哪一篇"，真正生效的 key 由 pickReportKey 派生——
  // 切组之后旧 key 通常不存在，派生值不可能滞后（理由见 reports.mjs 头注 2）。
  const [wantedKey, setWantedKey] = useState('');
  // 第三百零九批（SB-202 S3 余项）：标签片。null＝全部；选中的标签不在新清单里时由 pick 自然回落到全部。
  const [tag, setTag] = useState(null);

  const list = useLoad(async () => {
    // 这个面需要真整数组 id（汇报按组分片，没有 slug 回落这一说），
    // 所以"没有当前组"是一个要渲染出来的状态，不是一次静默的空列表。
    if (!currentGroup || currentGroup.id == null) return { noGroup: true, reports: [] };
    const r = await api.projectReports(currentGroup.id);
    // projectGroupId 随清单一起留下：详情请求据它取组（见下方 listedGroupId 的
    // 理由）。取服务端回的那个而不是发出去的那个——它们相等，但相等这件事该由
    // 服务端说，不该由客户端假设。
    return { noGroup: false, reports: normalizeReports(r),
             projectGroupId: r?.projectGroupId ?? currentGroup.id };
  }, [currentGroup?.id]);

  const allReports = list.data?.reports ?? [];
  const tags = tagsOf(allReports);
  const activeTag = tag !== null && tags.includes(tag) ? tag : null;
  const reports = filterByTag(allReports, activeTag);
  const selectedKey = pickReportKey(reports, wantedKey);
  // 详情必须**跟着产出这份清单的那个组**走，而不是跟着实时的 currentGroup
  //（第一百零二批对抗审查坐实）：切组的一瞬间 currentGroup.id 已是新组、而
  // selectedKey 还是旧组的 key，两者拼出的请求必然 404，界面先闪一下报错再自愈。
  // 清单响应自带 projectGroupId，用它就天然同步——组与 key 永远来自同一次加载。
  const listedGroupId = list.data?.projectGroupId ?? null;

  const detail = useLoad(async () => {
    if (listedGroupId == null || !selectedKey) return null;
    return api.projectReport(selectedKey, listedGroupId);
  }, [listedGroupId, selectedKey]);

  const doc = detail.data;
  const revisions = normalizeRevisions(doc);

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('reports.title')}
        <HelpLink section="reports" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reports.subtitle')}
      </Typography>

      {/* ── 空态三分：各说得出原因 ────────────────────────────────────────
          "没有当前组"（会话里一个组都没有）、"这个组还没有汇报"（组是对的，
          东西还没写）、"加载失败"（网络/会话/服务端）——三件事的下一步动作
          完全不同，合成一句"暂无数据"等于把用户该做什么这件事也一起抹掉。
          写法照 WorkItemsPage。 */}
      {list.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(list.error, { fallback: 'reports.error' }))}
          </Alert>
          <Button size="small" onClick={list.reload}>{t('common.retry')}</Button>
        </Box>
      ) : list.data?.noGroup ? (
        <Alert severity="warning">{t('reports.noGroup')}</Alert>
      ) : allReports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('reports.empty')}</Typography>
        </Paper>
      ) : (
        <Box>
        {/* 标签片：只有清单里出现过两种以上标签时才值得占一行（单族时片是废话）。 */}
        {tags.length > 1 ? (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }} data-report-tags="1">
            <Chip size="small" label={t('reports.tag.all')} color={activeTag === null ? 'primary' : 'default'}
                  variant={activeTag === null ? 'filled' : 'outlined'} onClick={() => setTag(null)} />
            {tags.map((tg) => (
              <Chip key={tg || '(other)'} size="small" label={tg || t('reports.tag.other')} data-report-tag={tg}
                    color={activeTag === tg ? 'primary' : 'default'} variant={activeTag === tg ? 'filled' : 'outlined'}
                    onClick={() => setTag(tg)} />
            ))}
          </Stack>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* ── 左：清单 ─────────────────────────────────────────────────
              窄屏也给高度上限：一个组的汇报篇数没有硬上界，让它整列铺开会把
              正文顶到好几屏之下（DocBrowserPage 的树为同一件事付过账）。 */}
          <Paper
            variant="outlined"
            sx={{
              width: { xs: '100%', md: 320 },
              flexShrink: 0,
              maxHeight: { xs: '40vh', md: 'calc(100vh - 220px)' },
              overflow: 'auto',
              alignSelf: { md: 'flex-start' },
            }}
          >
            <List disablePadding>
              {reports.map((r) => (
                <ListItemButton
                  key={r.reportKey}
                  selected={r.reportKey === selectedKey}
                  onClick={() => setWantedKey(r.reportKey)}
                  sx={{ display: 'block', py: 1 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {r.title}
                    </Typography>
                    {r.viaAgent ? <AgentChip /> : null}
                  </Stack>
                  {/* key 与标题分行显示：key 是 agent 与 CLI 那侧的地面真值
                      （`report put <key>` 写的就是它），出问题时要报的是它。 */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'ui-monospace, monospace', display: 'block', overflowWrap: 'anywhere' }}
                  >
                    {r.reportKey}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('reports.revision', { n: r.revision })}
                    {r.updatedAtMs ? ` · ${fmtTime(r.updatedAtMs, lang)}` : ''}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>

          {/* ── 右：正文 ──────────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3 } }}>
            {detail.status === 'loading' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : detail.status === 'error' ? (
              <Box>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {t(errKey(detail.error, { byStatus: { 404: 'reports.gone' },
                                            fallback: 'reports.detailError' }))}
                </Alert>
                <Button size="small" onClick={detail.reload}>{t('common.retry')}</Button>
              </Box>
            ) : !doc ? (
              <Typography color="text.secondary">{t('reports.selectHint')}</Typography>
            ) : (
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 0.5 }}
                >
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {doc.title || doc.reportKey}
                  </Typography>
                  {doc.viaAgent ? <AgentChip /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {t('reports.revision', { n: Math.max(0, Math.trunc(Number(doc.revision)) || 0) })}
                  {doc.updatedAtMs ? ` · ${t('reports.updated', { time: fmtTime(doc.updatedAtMs, lang) })}` : ''}
                </Typography>

                <MarkdownBody body={doc.body} format={doc.bodyFormat} />

                {/* ── 最近修订（只读）───────────────────────────────────
                    只列"第几版、什么时候、是不是 agent 写的"。**不给取旧版正文
                    的入口**：后端的 history 端点本来就只回这四个字段，页面上摆一个
                    点不开的链接，比不摆更糟。 */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t('reports.revisions')}
                </Typography>
                {revisions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('reports.revisionsEmpty')}
                  </Typography>
                ) : (
                  // 刻意不用 List/ListItemText：ListItemText 会把 primary 包进一个
                  // <Typography component="span">，而这一行里要放 Stack（div）——
                  // div 套在 span 里是无效嵌套，React 会在开发构建里报 DOM 警告。
                  <Stack spacing={0.75}>
                    {revisions.map((h) => (
                      <Box key={`${h.revision}-${h.atMs}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('reports.revision', { n: h.revision })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {h.atMs ? fmtTime(h.atMs, lang) : '—'}
                          </Typography>
                          {h.viaAgent ? <AgentChip /> : null}
                        </Stack>
                        {/* 标题随版本变过时，这一行就是"那一版叫什么"的唯一线索。 */}
                        {h.title ? (
                          <Typography variant="caption" color="text.secondary"
                                      sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {h.title}
                          </Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Paper>
        </Box>
        </Box>
      )}
    </Box>
  );
}
